import { useContext, useEffect, useRef, useState } from 'react';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  useGetTokenExIframeSrc,
  ValidateHandlerEvent,
} from '@ltvco/refresh-lib/payments';

interface UseTokenExIframeReturn {
  Iframe: any;
  isLoading: boolean;
  isError: boolean;
  iframeExpired: boolean;
  iframeInputInvalid: boolean;
  isIframeEmpty: boolean;
  tokenExLabelRef: React.RefObject<HTMLLabelElement>;
  tokenExWrapperRef: React.RefObject<HTMLDivElement>;
  handleTokenization: () => void;
  createEventHandlers: (
    onSubmit: (formData: any, token: string, maskedNumber: string) => void,
    options?: { ctaClickedRef?: React.MutableRefObject<boolean> }
  ) => any;
}

export const useTokenExIframe = (): UseTokenExIframeReturn => {
  const { logError } = useContext(AppConfig);
  const { data: Iframe, isLoading, isError } = useGetTokenExIframeSrc();
  const [iframeExpired, setIframeExpired] = useState<boolean>(false);
  const [iframeInputInvalid, setIframeInputInvalid] = useState<boolean>(false);
  const [isPristine, setIsPristine] = useState<boolean>(true);
  const [isIframeEmpty, setIsIframeEmpty] = useState<boolean>(true);
  const tokenExLabelRef = useRef<HTMLLabelElement>(null);
  const tokenExWrapperRef = useRef<HTMLDivElement>(null);
  const iframeRemovedRef = useRef<boolean>(false);
  const iframeLoadRetryCount = useRef<number>(0);
  const MAX_IFRAME_LOAD_RETRIES = 10; // Maximum retries for iframe loading

  // Initialize tokenEx Iframe
  useEffect(() => {
    if (!isLoading && !isError && Iframe) {
      iframeRemovedRef.current = false; // Reset removal flag for new iframe
      iframeLoadRetryCount.current = 0; // Reset retry count for new iframe

      // Wait for DOM element to be available before loading iframe
      const checkAndLoadIframe = () => {
        const container = document.getElementById('tokenex-iframe-target');
        if (container) {
          try {
            // Verify the container is properly attached to the DOM
            if (container.parentNode) {
              try {
                // Try to configure the iframe with the container ID if the method exists
                if (typeof Iframe.configure === 'function') {
                  Iframe.configure({
                    containerId: 'tokenex-iframe-target',
                  });
                }
                Iframe.load?.();
                iframeLoadRetryCount.current = 0; // Reset retry count on success
              } catch (configError) {
                // If configure fails, try loading without it
                Iframe.load?.();
                iframeLoadRetryCount.current = 0;
              }
            } else {
              logError(
                'TokenEx iframe container not properly attached to DOM',
                new Error('Container found but not attached to parent')
              );
            }
          } catch (error) {
            logError('Error loading TokenEx iframe', error as Error);
          }
        } else if (iframeLoadRetryCount.current < MAX_IFRAME_LOAD_RETRIES) {
          // Retry after a short delay if container not found
          iframeLoadRetryCount.current += 1;
          setTimeout(checkAndLoadIframe, 100);
        } else {
          logError(
            'TokenEx iframe container not found after maximum retries',
            new Error('Container ID: tokenex-iframe-target')
          );
        }
      };

      checkAndLoadIframe();
    }

    const timeoutId = setTimeout(() => {
      document
        .getElementById('tokenex-iframe-target')
        ?.setAttribute('tabindex', '3');
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      // Only remove if iframe exists, has a remove method, and hasn't been removed yet
      if (
        Iframe &&
        typeof Iframe.remove === 'function' &&
        !iframeRemovedRef.current
      ) {
        try {
          Iframe.remove();
          iframeRemovedRef.current = true;
        } catch (error) {
          iframeRemovedRef.current = true;
          logError('Error removing TokenEx iframe', error as Error);
        }
      }
    };
  }, [Iframe, isLoading, isError, logError]);

  // TokenEx message event handlers
  const createEventHandlers = (
    onSubmitCallback: (
      formData: any,
      token: string,
      maskedNumber: string
    ) => void,
    options?: { ctaClickedRef?: React.MutableRefObject<boolean> }
  ) => {
    return {
      loadHandler() {},
      onIframeExpired() {
        setIframeExpired(true);
      },
      validateHandler(event: ValidateHandlerEvent) {
        // The actual data structure from TokenEx has the validation data nested under event.data.data
        const validationData = (event.data as any).data ?? event.data;
        const { validator } = validationData;
        const { isValid } = validationData;

        // If CTA clicked while pristine, surface invalid styling immediately
        if (options?.ctaClickedRef?.current && isPristine) {
          tokenExLabelRef.current?.classList.toggle('isInvalid', true);
          setIframeInputInvalid(true);
          tokenExWrapperRef.current?.classList.toggle('error', true);
        }

        // Mirror RadioPayments behavior
        if (validator !== 'required' && isPristine) {
          setIsIframeEmpty(false);
          setIsPristine(false);
          setIframeInputInvalid(!isValid);
          tokenExWrapperRef.current?.classList.toggle('error', !isValid);
          tokenExLabelRef.current?.classList.toggle('has-value', true);
          tokenExLabelRef.current?.classList.toggle('isInvalid', !isValid);
          return;
        }

        if (validator === 'required' && isPristine) {
          setIsIframeEmpty(true);
          return;
        }

        if (validator === 'required' && !isPristine) {
          setIsIframeEmpty(true);
          setIframeInputInvalid(true);
          tokenExLabelRef.current?.classList.toggle('has-value', true);
          tokenExLabelRef.current?.classList.toggle('isFocused', false);
          tokenExWrapperRef.current?.classList.toggle('error', true);
          tokenExLabelRef.current?.classList.toggle('isInvalid', true);
          return;
        }

        if (validator !== 'required' && !isPristine) {
          setIsIframeEmpty(false);
          const inputHasValue = validator !== 'required';
          setIframeInputInvalid(!isValid);
          tokenExLabelRef.current?.classList.toggle('has-value', inputHasValue);
          tokenExLabelRef.current?.classList.toggle('isInvalid', !isValid);
          tokenExWrapperRef.current?.classList.toggle('error', !isValid);
          return;
        }
      },
      tokenizeHandler(
        event: MessageEvent<{
          firstSix: string;
          lastFour: string;
          referenceNumber: string;
          token: string;
          tokenHMAC: string;
          cardType: string;
        }>
      ) {
        // The actual data structure from TokenEx has the token data nested under event.data.data
        const tokenData = (event.data as any).data ?? event.data;
        const { token, firstSix, lastFour, cardType } = tokenData;

        if (token && firstSix && lastFour) {
          // Construct maskedNumber like the working component does
          const exesForType = (cardType: string) => {
            const cardTypeMap: Record<string, string> = {
              visa: '******',
              masterCard: '******',
              americanExpress: '******',
              discover: '******',
            };
            return cardTypeMap[cardType] || '******';
          };

          const maskedNumber = firstSix + exesForType(cardType) + lastFour;

          onSubmitCallback({}, token, maskedNumber);
        } else {
          logError(
            'Missing required data for tokenization',
            new Error(
              `token:${!!token}, firstSix:${!!firstSix}, lastFour:${!!lastFour}`
            )
          );
        }
      },
      focusHandler() {
        tokenExLabelRef.current?.classList.add('isFocused');
        tokenExWrapperRef.current?.classList.add('focus');
      },
      blurHandler() {
        tokenExLabelRef.current?.classList.remove('isFocused');
        tokenExWrapperRef.current?.classList.remove('focus');
        if (isPristine || isIframeEmpty) {
          tokenExLabelRef.current?.classList.remove('has-value');
        }
      },
    };
  };

  const handleTokenization = () => {
    // Only tokenize if iframe exists and has tokenize method
    if (Iframe && typeof Iframe.tokenize === 'function') {
      try {
        Iframe.tokenize();
      } catch (error) {
        logError('Tokenization error', error as Error);
      }
    }
  };

  return {
    Iframe,
    isLoading,
    isError,
    iframeExpired,
    iframeInputInvalid,
    isIframeEmpty,
    tokenExLabelRef,
    tokenExWrapperRef,
    handleTokenization,
    createEventHandlers,
  };
};
